import React, { useState, useEffect } from 'react';
import { Input, Select, Button, Card, Tabs, Empty, Tooltip, Space, Badge } from 'antd';
import { SearchOutlined, BgColorsOutlined } from '@ant-design/icons';
import ColorSelect from '../FormColorSelect/ColorSelect';


const Icon = ({ className, pkg, iconName, color, size }) => {
  const [IconComp, setIcon] = useState();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  
  useEffect(() => {
    const loadIcon = async () => {
      if (!iconName || !pkg) return;
      try {
        setLoading(true);
        setError(false);
        const IconSets = await import(`/node_modules/react-icons/${pkg}/index.js`);
        setIcon(() => IconSets[iconName]);
      } catch (err) {
        console.error(`Failed to load icon ${pkg}/${iconName}:`, err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    loadIcon();
  }, [pkg, iconName]);
  
  if (loading) {
    return (
      <div 
        className={className} 
        style={{ 
          width: size, 
          height: size, 
          display: 'inline-flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <span style={{ fontSize: size / 3, color: '#999' }}>...</span>
      </div>
    );
  }
  
  if (error || !IconComp) {
    return (
      <div 
        className={className} 
        style={{ 
          width: size, 
          height: size, 
          display: 'inline-flex',
          justifyContent: 'center',
          alignItems: 'center',
          border: '1px dashed #d9d9d9',
          borderRadius: '4px'
        }}
      >
        <span style={{ fontSize: size / 3, color: '#ff4d4f' }}>!</span>
      </div>
    );
  }
  
  return <IconComp className={className} color={color} size={size} />;
}


const IconPicker = ({ value, onChange }) => {
  const [defaultValue, setDefaultValue] = useState(value || { size: 24, color: '#1890ff' });
  const [selectPkg, setSelectPkg] = useState(value?.pkg || 'ai');
  const [keywords, setKeywords] = useState('');
  const [activeTab, setActiveTab] = useState('ai');

  const handleIconSelect = ({ pkg, iconName }) => {
    setDefaultValue({ ...defaultValue, pkg, iconName });
  };

  const iconLibraries = [
    { label: "Ant Design Icons", value: "ai", popular: true },
    { label: "Bootstrap Icons", value: "bs", popular: true },
    { label: "Feather", value: "fi", popular: true },
    { label: "Heroicons", value: "hi", popular: true },
    { label: "Lucide", value: "lu", popular: true },
    { label: "Material Design", value: "md", popular: true },
    { label: "BoxIcons", value: "bi" },
    { label: "Circum Icons", value: "ci" },
    { label: "css.gg", value: "cg" },
    { label: "Devicons", value: "di" },
    { label: "Flat Color Icons", value: "fc" },
    { label: "Font Awesome 5", value: "fa" },
    { label: "Font Awesome 6", value: "fa6" },
    { label: "Game Icons", value: "gi" },
    { label: "Github Octicons", value: "go" },
    { label: "Grommet-Icons", value: "gr" },
    { label: "Heroicons 2", value: "hi2" },
    { label: "IcoMoon Free", value: "im" },
    { label: "Icons8 Line Awesome", value: "lia" },
    { label: "Ionicons 4", value: "io" },
    { label: "Ionicons 5", value: "io5" },
    { label: "Phosphor Icons", value: "pi" },
    { label: "Radix Icons", value: "rx" },
    { label: "Remix Icon", value: "ri" },
    { label: "Simple Icons", value: "si" },
    { label: "Simple Line Icons", value: "sl" },
    { label: "Tabler Icons", value: "tb" },
    { label: "Themify Icons", value: "tfi" },
    { label: "Typicons", value: "ti" },
    { label: "VS Code Icons", value: "vsc" },
    { label: "Weather Icons", value: "wi" },
  ];

  const tabItems = iconLibraries.map(lib => ({
    key: lib.value,
    label: (
      <Space>
        {lib.popular && <Badge color="#52c41a" />}
        {lib.label}
      </Space>
    ),
    children: (
      <div style={{ height: '400px', overflow: 'auto' }}>
        <IconGroup 
          onSelect={handleIconSelect} 
          pkg={lib.value} 
          keywords={keywords}
          selectedIcon={defaultValue}
        />
      </div>
    )
  }));

  return (
    <div className="icon-picker-container" style={{ height: '500px', display: 'flex', flexDirection: 'column' }}>
      {/* 顶部工具栏 */}
      <Card size="small" style={{ marginBottom: 16, flexShrink: 0 }}>
        <Space wrap>
          <Input.Search
            placeholder="搜索图标..."
            value={keywords}
            onChange={(e) => setKeywords(e.target.value)}
            onSearch={setKeywords}
            style={{ width: 200 }}
            prefix={<SearchOutlined />}
            allowClear
          />
          <span>大小:</span>
          <Select 
            value={defaultValue.size} 
            onChange={(val) => setDefaultValue({ ...defaultValue, size: val })}
            style={{ width: 80 }}
          >
            <Select.Option value={16}>16px</Select.Option>
            <Select.Option value={20}>20px</Select.Option>
            <Select.Option value={24}>24px</Select.Option>
            <Select.Option value={32}>32px</Select.Option>
            <Select.Option value={48}>48px</Select.Option>
          </Select>
          <span>颜色:</span>
          <ColorSelect 
            value={defaultValue.color} 
            onChange={(color) => setDefaultValue({ ...defaultValue, color })} 
          />
          {defaultValue.pkg && defaultValue.iconName && (
            <div style={{ 
              padding: '8px 12px', 
              border: '1px solid #d9d9d9', 
              borderRadius: '6px',
              background: '#fafafa'
            }}>
              <Space>
                <span>预览:</span>
                <Icon {...defaultValue} />
              </Space>
            </div>
          )}
        </Space>
      </Card>

      {/* 图标库选择和图标展示 */}
      <div style={{ flex: 1, minHeight: 0 }}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          type="card"
          size="small"
          tabPosition="left"
          style={{ height: '100%' }}
          items={tabItems}
        />
      </div>

      {/* 底部操作按钮 */}
      <div style={{ 
        marginTop: 16, 
        textAlign: 'right',
        borderTop: '1px solid #f0f0f0',
        paddingTop: 16,
        flexShrink: 0
      }}>
        <Space>
          <Button onClick={() => onChange && onChange(value)}>
            取消
          </Button>
          <Button 
            type="primary" 
            disabled={!defaultValue.pkg || !defaultValue.iconName}
            onClick={() => onChange && onChange(defaultValue)}
          >
            确定选择
          </Button>
        </Space>
      </div>
    </div>
  );
};

const IconGroup = ({ pkg, onSelect, keywords, selectedIcon }) => {
    const [iconSet, setIconSet] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadIconSet = async () => {
            try {
                setLoading(true);
                const icons = (await import(`/node_modules/react-icons/${pkg}/index.js`)).default;
                setIconSet(icons);
            } catch (error) {
                console.error(`Failed to load icon set ${pkg}:`, error);
                setIconSet({});
            } finally {
                setLoading(false);
            }
        };
        loadIconSet();
    }, [pkg]);

    if (loading) {
        return (
            <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '200px' 
            }}>
                <span>加载中...</span>
            </div>
        );
    }

    if (!iconSet) {
        return (
            <Empty 
                description="图标库加载失败" 
                style={{ marginTop: 50 }}
            />
        );
    }

    const iconNames = keywords && keywords !== "" 
        ? Object.keys(iconSet).filter(name => 
            name.toLowerCase().includes(keywords.toLowerCase())
          )
        : Object.keys(iconSet);

    if (iconNames.length === 0) {
        return (
            <Empty 
                description={keywords ? `未找到包含 "${keywords}" 的图标` : "暂无图标"} 
                style={{ marginTop: 50 }}
            />
        );
    }

    return (
        <div style={{ 
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(60px, 1fr))',
            gap: '8px',
            padding: '16px'
        }}>
            {iconNames.map((iconName, index) => {
                const IconComponent = iconSet[iconName];
                const isSelected = selectedIcon?.pkg === pkg && selectedIcon?.iconName === iconName;
                
                return (
                    <Tooltip key={index} title={iconName} placement="bottom">
                        <div 
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '60px',
                                height: '60px',
                                border: isSelected ? '2px solid #1890ff' : '1px solid #f0f0f0',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                backgroundColor: isSelected ? '#e6f7ff' : '#fafafa',
                                transition: 'all 0.2s ease',
                                ':hover': {
                                    borderColor: '#1890ff',
                                    backgroundColor: '#f0f9ff'
                                }
                            }}
                            onClick={() => onSelect({ pkg, iconName })}
                            onMouseEnter={(e) => {
                                if (!isSelected) {
                                    e.target.style.borderColor = '#1890ff';
                                    e.target.style.backgroundColor = '#f0f9ff';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (!isSelected) {
                                    e.target.style.borderColor = '#f0f0f0';
                                    e.target.style.backgroundColor = '#fafafa';
                                }
                            }}
                        >
                            <IconComponent size={24} color={isSelected ? '#1890ff' : '#666'} />
                        </div>
                    </Tooltip>
                );
            })}
        </div>
    );
};

export { IconPicker, Icon };
